import { keyframes, styled } from "styled-components";
import { getIntervalAmount } from "../../Functions/incrementAmount";
import { useEffect, useRef, useState } from "react";

interface MobileBarContainerProps {
    $height: number;
    $width: number;
}

interface MobileBarBoundsProps {
    $height: number;
}

interface MobileValueProps {
    $fontSize: number;
    $left: number;
}

interface MobileOuterBarProps {
    $height: number;
}

interface MobileInnerBarProps {
    $height: number;
    $width: number;
    $animationTime: number;
}

interface BottomLabelProps {
    $left: number;
    $bottom: number;
    $fontSize: number;
}

const MobileInnerBarKeyframes = (props: number) => keyframes`
    0% {
            height: 0%;
        }
    100% {
        height: ${props}%;
    }
`;

const MobileBarContainer = styled.div<MobileBarContainerProps>`
    display: flex;
    transition: background ${(props) => props.theme.animationTime.short}s;
    flex-direction: column;
    height: ${(props) => props.$height}%;
    width: ${(props) => props.$width}%;
    overflow: show;
`;

const MobileBarMaxValueLabel = styled.div<MobileBarBoundsProps>`
    height: ${(props) => props.$height}px;
    text-align: center;
`;
const MobileBarOuterBar = styled.div<MobileOuterBarProps>`
    height: ${(props) => props.$height}px;
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
`;
const MobileBarInnerBar = styled.div<MobileInnerBarProps>`
    background: ${(props) => props.theme.gradient.defaultVerticle};
    animation: ${(props) => MobileInnerBarKeyframes(props.$height)}
        ${(props) => props.$animationTime}s;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
    width: ${(props) => props.$width}%;
    border-radius: 2px 2px 0px 0px;
`;
const MobileBarMinValueLabel = styled.div<MobileBarBoundsProps>`
    height: ${(props) => props.$height}px;
    text-align: center;
`;
const MobileBarValueLabel = styled.div<MobileValueProps>`
    overflow: show;
    width: 100%;
    position: relative;
    text-align: right;
    font-size: ${(props) => props.$fontSize * 0.5}px;
    top: -${(props) => props.$fontSize * 1.5}px;
    left: -${(props) => props.$left * 0.15}px;
    font-weight: 600;
`;
const MobileBarLabel = styled.div<BottomLabelProps>`
    position: relative;
    transform: rotate(-90deg);
    text-align: center;
    left: ${(props) => -140 / 2.2}px;
    bottom: ${(props) => props.$bottom * 0.8}px;
    font-size: ${(props) => props.$fontSize}px;
    height: ${(props) => props.$fontSize * 1.2}px;
    width: 140px;
    overflow: hidden;
    white-space: nowrap;
    text-align: right;
    text-transform: uppercase;
    font-weight: 600;
`;

const MobileBarAnalyticsDefaults = {
    percent: 100,
    lowerBound: "0",
    upperBound: "100",
    width: 5,
    height: 100,
    animationTimeBar: 3,
    max: 100,
    showVerticalBar: true,
    bottomLabelFineTune: 0.83,
    bottomLabelXFineTune: 1,
    bottomLabelYFineTune: 1,
    fontSize: 18,
};
interface MobileBarAnalyticsProps {
    percent?: number;
    lowerBound?: string;
    upperBound?: string;
    width?: number;
    height?: number;
    animationTimeBar?: number;
    max?: number;
    displayValue: boolean;
    label: string;
    showVerticalBar?: boolean;
    bottomLabelFineTune?: number;
    bottomLabelXFineTune?: number;
    bottomLabelYFineTune?: number;
    fontSize?: number;
}

export const MobileBarAnalytics: React.FC<MobileBarAnalyticsProps> = ({
    width = MobileBarAnalyticsDefaults.width,
    percent = MobileBarAnalyticsDefaults.percent,
    animationTimeBar = MobileBarAnalyticsDefaults.animationTimeBar,
    height = MobileBarAnalyticsDefaults.height,
    bottomLabelFineTune = MobileBarAnalyticsDefaults.bottomLabelFineTune,
    bottomLabelXFineTune = MobileBarAnalyticsDefaults.bottomLabelXFineTune,
    bottomLabelYFineTune = MobileBarAnalyticsDefaults.bottomLabelYFineTune,
    fontSize = MobileBarAnalyticsDefaults.fontSize,
    upperBound = MobileBarAnalyticsDefaults.upperBound,
    lowerBound = MobileBarAnalyticsDefaults.lowerBound,
    label = " ",
    displayValue,
    max = MobileBarAnalyticsDefaults.max,
}) => {
    // label at the bottom
    // outer bar
    // inner filled bar
    // value label at top of bar

    //everything will be backwards...

    // top... end of bar label
    // middle... the bar and inner bar
    // bottom.. start of bar label
    // bottom below... text label for bar
    const containerRef = useRef<HTMLDivElement>(null);
    const valueLabelRef = useRef<HTMLDivElement>(null);

    const [totalHeight, setTotalHeight] = useState<number>();
    const [boundSpace, setTotalBoundSpace] = useState<number>(0);
    const [labelSpace, setLabelSpace] = useState<number>(0);
    const [barSpace, setTotalBarSpace] = useState<number>(0);
    const [currVal, setCurrVal] = useState<number>(0);
    const [leftOffsetLabel, setLeftOffsetLabel] = useState<number>(0);
    const [bottomOffsetLabel, setBottomOffsetLabel] = useState<number>(0);
    const [valueLabelWidth, setvalueLabelWidth] = useState<number>(0);

    const intervalGap: number = 50;

    const incremAmount: number = getIntervalAmount({
        propsPercent: percent,
        defaultPercent: MobileBarAnalyticsDefaults.percent,
        propsAnimationTime: animationTimeBar,
        defaultAnimationTime: MobileBarAnalyticsDefaults.animationTimeBar,
        intervalAmount: intervalGap,
    });

    useEffect(() => {
        const valueLoop: NodeJS.Timer = setInterval(() => {
            const tempCurrValue: number = currVal
                ? currVal + incremAmount / 100
                : 0 + incremAmount / 100;
            setCurrVal(tempCurrValue);
        }, intervalGap);
        if (currVal ? currVal >= percent : 0 >= percent) {
            clearInterval(valueLoop);
        }
        return () => {
            clearInterval(valueLoop);
        };
    }, [currVal, incremAmount, percent]);

    useEffect(() => {
        containerRef.current
            ? setTotalHeight(containerRef.current.offsetHeight)
            : setTotalHeight(0);
        containerRef.current
            ? setLeftOffsetLabel(
                  containerRef.current.offsetLeft -
                      containerRef.current.offsetWidth * 2
              )
            : setLeftOffsetLabel(0);
        containerRef.current
            ? setBottomOffsetLabel(
                  containerRef.current.offsetHeight -
                      labelSpace -
                      barSpace -
                      boundSpace * 2
              )
            : setBottomOffsetLabel(0);
    }, [containerRef, fontSize, width, labelSpace, barSpace, boundSpace]);

    useEffect(() => {
        valueLabelRef.current
            ? setvalueLabelWidth(valueLabelRef.current.offsetWidth)
            : setvalueLabelWidth(0);
    }, [valueLabelRef]);

    useEffect(() => {
        totalHeight ? setTotalBarSpace(totalHeight * 0.6) : setTotalBarSpace(0);
        totalHeight
            ? setTotalBoundSpace(totalHeight * 0.1)
            : setTotalBoundSpace(0);
        totalHeight ? setLabelSpace(totalHeight * 0.2) : setLabelSpace(0);
    }, [totalHeight]);
    return (
        <MobileBarContainer $height={height} ref={containerRef} $width={width}>
            <MobileBarMaxValueLabel $height={boundSpace}>
                {upperBound}
            </MobileBarMaxValueLabel>
            <MobileBarOuterBar $height={barSpace}>
                <MobileBarInnerBar
                    $height={percent}
                    $width={90}
                    $animationTime={animationTimeBar}
                >
                    <MobileBarValueLabel
                        $fontSize={fontSize}
                        $left={valueLabelWidth}
                        ref={valueLabelRef}
                    >
                        {displayValue ? Math.round(currVal * (max / 100)) : ""}
                    </MobileBarValueLabel>
                </MobileBarInnerBar>
            </MobileBarOuterBar>
            <MobileBarMinValueLabel $height={boundSpace}>
                {lowerBound}
            </MobileBarMinValueLabel>
            <MobileBarLabel
                $bottom={bottomOffsetLabel * bottomLabelYFineTune}
                $left={leftOffsetLabel * bottomLabelXFineTune}
                $fontSize={fontSize}
            >
                {label}
            </MobileBarLabel>
        </MobileBarContainer>
    );
};
